import { FloatingSearch } from "@/components/ui";
import { Messages } from "@/components/ui";
import { useDebounce } from "@/hooks/useDebounce";
import { getGroupQueryOptions } from "@/queries";
import {
	CHAT_GROUP_TABS,
	ChatGroupSearchSchema,
	DataIdSchema,
	PostCreateMessageBodySchema,
} from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import AppBarGroupChat from "../../-components/AppBarGroup";
import EmptyChat from "../../-components/Empty";

export const Route = createFileRoute("/dashboard/chat/group/$id/")({
	params: {
		parse: (params) => ({
			// @ts-expect-error
			id: DataIdSchema.parse(Number(params.id)),
		}),
		stringify: ({ id }) => ({ id: id.toString() }),
	},
	validateSearch: zodSearchValidator(ChatGroupSearchSchema),
	component: RouteComponent,
});

const ChatGroupTabs = [
	{
		label: "Chat",
		value: CHAT_GROUP_TABS.ALL,
	},
	{
		label: "HEMI AI",
		value: CHAT_GROUP_TABS.AI,
	},
	{
		label: "Question",
		value: CHAT_GROUP_TABS.QUESTION,
	},
	{
		label: "Topic",
		value: CHAT_GROUP_TABS.TOPIC,
	},
	{
		label: "Media",
		value: CHAT_GROUP_TABS.MEDIA,
	},
	{
		label: "Published",
		value: CHAT_GROUP_TABS.PUBLISHED,
	},
];

function RouteComponent() {
	const navigate = Route.useNavigate();
	const groupId = Route.useParams({
		select: (params) => params.id,
	});
	const { search, tab } = Route.useSearch({
		select: (search) => ({
			search: search.q || "",
			tab: search.tab,
		}),
	});
	const debounceQ = useDebounce(search, 500);
	const queryGroup = useSuspenseQuery(getGroupQueryOptions(groupId));
	const channelName = queryGroup.data?.group?.channel_name;

	const channelMembers = useMemo(() => {
		return [
			...queryGroup.data.group.members,
			...queryGroup.data.group.pending_invitations,
		];
	}, [queryGroup.data]);

	const form = useForm<PostCreateMessageBodySchema>({
		mode: "all",
		resolver: zodResolver(PostCreateMessageBodySchema),
		defaultValues: async () => ({
			channel_name: channelName,
		}),
	});

	const onChangeTab = (val: string) => {
		return navigate({
			search: (oldSearch: ChatGroupSearchSchema) => ({
				...oldSearch,
				tab: val,
			}),
			replace: true,
		});
	};

	const onChangeSearch = (val: string) => {
		return navigate({
			search: (oldSearch: ChatGroupSearchSchema) => ({
				...oldSearch,
				q: val || undefined,
			}),
			replace: true,
		});
	};

	return (
		<>
			<AppBarGroupChat
				type="group"
				id={groupId}
				title={queryGroup.data.group.name}
				tabs={ChatGroupTabs}
				activeTab={tab}
				onChangeTab={onChangeTab}
			/>
			<div className="relative container flex gap-1 justify-end py-3">
				<FloatingSearch
					name="search"
					value={search}
					onClear={() => onChangeSearch("")}
					onChange={(e) => onChangeSearch(e.target.value)}
				/>
			</div>
			<FormProvider {...form}>
				<Messages
					module="chats"
					channelType="group"
					channelName={channelName}
					members={channelMembers}
					search={{
						search: debounceQ,
						tab,
					}}
					renderEmpty={<EmptyChat />}
				/>
			</FormProvider>
		</>
	);
}
